import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin, switchMap } from 'rxjs';
import { JobsService } from '../../../services/jobs.service';
import { ProjectsService } from '../../../services/projects.service';
import { SaveJobDto } from '@models/save-job-dto.interface';
import { ProjectTreeNode } from '@models/project-tree-node.interface';
import { PROJECT_NODE_TYPE } from '@models/project-node-type.const';
import { CommentsComponent } from '@shared/components/comments/comments.component';
import { JobNotesProvider } from '../../../services/job-notes-provider.service';

export interface EditJobDialogData {
  jobId?: number;
  projectId?: number;
  year?: number | null;
}

interface ProjectOption {
  id: number;
  name: string;
}

interface JobTypeOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-edit-job',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    CommentsComponent,
  ],
  templateUrl: './edit-job.component.html',
  styleUrl: './edit-job.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditJobComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<EditJobComponent>);
  protected readonly data = inject<EditJobDialogData>(MAT_DIALOG_DATA);
  private readonly jobsService = inject(JobsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly jobNotesProvider = inject(JobNotesProvider);

  protected readonly isCreate = this.data.jobId == null;
  protected isLoading = signal(true);
  protected isSaving = signal(false);
  protected availableProjects = signal<ProjectOption[]>([]);
  protected readonly jobTypes: JobTypeOption[] = [
    { id: 1, name: 'Drilling' },
    { id: 2, name: 'Completion' },
    { id: 3, name: 'Cement' },
    { id: 99, name: 'Other' },
  ];

  protected form = this.fb.group({
    name: ['', Validators.required],
    description: [''],
    initiatedOn: ['' as string, Validators.required],
    endDate: ['' as string],
    projectId: [0, [Validators.required, Validators.min(1)]],
    sapCode: [''],
    jobType: [1],
  });

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  ngOnInit(): void {
    if (this.isCreate) {
      this.loadProjectsForCreate();

      this.form.get('projectId')!.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((projectId) => {
        if (this.isLoading()) return;
        this.syncSapCodeFromProject(projectId);
      });
    } else {
      this.loadJobForEdit();
    }
  }

  private loadProjectsForCreate(): void {
    const year = this.data.year ?? new Date().getFullYear();
    forkJoin({
      tree: this.projectsService.getProjectTree(year),
      nextJob: this.jobsService.getNextJobName(),
    }).subscribe({
      next: ({ tree, nextJob }) => {
        const projects = this.extractProjects(tree);
        this.availableProjects.set(projects);
        const today = new Date().toISOString().substring(0, 10);
        const preselectedProjectId = this.data.projectId ?? (projects[0]?.id ?? 0);
        this.form.patchValue({
          name: nextJob.name,
          initiatedOn: today,
          projectId: preselectedProjectId,
        });
        this.isLoading.set(false);

        if (preselectedProjectId) {
          this.syncSapCodeFromProject(preselectedProjectId);
        }
      },
      error: () => this.isLoading.set(false),
    });
  }

  private syncSapCodeFromProject(projectId: number): void {
    if (!projectId || this.form.get('sapCode')?.dirty) return;

    this.projectsService.getProject(projectId).subscribe({
      next: project => {
        if (!this.form.get('sapCode')?.dirty) {
          this.form.get('sapCode')!.patchValue(project.sapCode ?? '', { emitEvent: false });
        }
      },
    });
  }

  private loadJobForEdit(): void {
    this.jobsService.getJobForEdit(this.data.jobId!).pipe(
      switchMap(job => {
        const year = job.initiatedOn ? new Date(job.initiatedOn).getFullYear() : null;
        return this.projectsService.getProjectTree(Number.isNaN(year) ? null : year)
          .pipe(switchMap(tree => [{ job, tree }]));
      })
    ).subscribe({
      next: ({ job, tree }) => {
        this.availableProjects.set(this.extractProjects(tree));
        this.form.patchValue({
          name: job.name,
          description: job.description ?? '',
          initiatedOn: job.initiatedOn ? job.initiatedOn.substring(0, 10) : '',
          endDate: job.endDate ? job.endDate.substring(0, 10) : '',
          projectId: job.projectId,
          sapCode: job.sapCode ?? '',
          jobType: job.jobType ?? 1,
        });
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  private extractProjects(tree: ProjectTreeNode[]): ProjectOption[] {
    const orgLookupNodes = tree.filter(n => !(n.key ?? '').startsWith('rig-'));
    return orgLookupNodes
      .flatMap(buNode => buNode.children
        ?.filter(n => n.data?.nodeType === PROJECT_NODE_TYPE.PROJECT)
        .map(n => ({ id: n.data.entityId ?? 0, name: n.label ?? '' })) ?? []
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  protected onSave(): void {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const dto: SaveJobDto = {
      name: raw.name ?? '',
      description: raw.description || undefined,
      sapCode: raw.sapCode || undefined,
      jobType: raw.jobType ?? 1,
      projectId: Number(raw.projectId ?? 0),
      initiatedOn: raw.initiatedOn ? new Date(raw.initiatedOn).toISOString() : '',
      endDate: raw.endDate ? new Date(raw.endDate).toISOString() : undefined,
    };

    this.isSaving.set(true);
    if (this.isCreate) {
      this.jobsService.createJob(dto).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.dialogRef.close(true);
        },
        error: () => this.isSaving.set(false),
      });
    } else {
      this.jobsService.updateJob(this.data.jobId!, dto).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.dialogRef.close(true);
        },
        error: () => this.isSaving.set(false),
      });
    }
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }
}
